import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL, EMAIL_API_URL } from "../../config";
import {
  Form, Input, Select, Upload, Checkbox, Tooltip,
  Button, Tag, Alert, Divider, Avatar, message,
} from "antd";
import {
  UserOutlined, SearchOutlined, ThunderboltOutlined,
  PaperClipOutlined, InboxOutlined, SendOutlined,
  EditOutlined, CheckCircleOutlined, BoldOutlined,
  ItalicOutlined, UnderlineOutlined, OrderedListOutlined,
  UnorderedListOutlined, AlignLeftOutlined, AlignCenterOutlined,
  AlignRightOutlined, LinkOutlined, UndoOutlined, RedoOutlined,
  CodeOutlined, ArrowLeftOutlined, EyeOutlined,
} from "@ant-design/icons";
import { authHeaders, getSessionToken } from "../utils/session";
import ComposeModeCards from "./ComposeModeCards";
import WidgetBodyEditor from "../email-widgets/WidgetBodyEditor";
import WidgetFrame from "../email-widgets/WidgetFrame";
import { previewWidget, hasGreetingSlot, DEFAULT_GREETING, SAMPLE_BODY, SAMPLE_SUBJECT } from "../email-widgets/widgetPreview";
import { useActiveEmailWidgets } from "../hooks/useEmailWidgets";

const { Option } = Select;
const { Dragger } = Upload;
const LIMIT = 50;

const plainToHtml = (text) =>
  text.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");

// Strip <style>/<script> tags so full HTML templates don't leak into the page
// when injected into a contentEditable div.
const sanitizeForCompose = (html) => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("style, script, link[rel='stylesheet']").forEach((el) => el.remove());
    return doc.body.innerHTML || "";
  } catch {
    return html;
  }
};

// ── Body Editor (Compose | HTML | Preview) ────────────────────────────────────
function BodyEditor({ value, onChange, placeholder, bodyMode, setBodyMode }) {
  const editorRef = useRef(null);
  const isInternal = useRef(false);

  // Sync value → contentEditable (sanitized) when switching to compose mode
  useEffect(() => {
    if (bodyMode === "compose" && editorRef.current) {
      editorRef.current.innerHTML = sanitizeForCompose(value || "");
    }
  }, [bodyMode]); // eslint-disable-line

  // Keep contentEditable in sync when value changes externally (sanitized)
  useEffect(() => {
    if (!editorRef.current || bodyMode !== "compose") return;
    const sanitized = sanitizeForCompose(value || "");
    if (editorRef.current.innerHTML !== sanitized && !isInternal.current)
      editorRef.current.innerHTML = sanitized;
    isInternal.current = false;
  }, [value]); // eslint-disable-line

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    isInternal.current = true;
    onChange?.(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => { editorRef.current?.focus(); document.execCommand(cmd, false, val); emit(); };

  const btn = (icon, cmd, val, title) => (
    <Tooltip title={title} key={cmd + (val || "")}>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(cmd, val); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
        onMouseLeave={(e) => e.currentTarget.style.background = "none"}
      >{icon}</button>
    </Tooltip>
  );

  const MODES = [
    { key: "compose", icon: <EditOutlined />, label: "Compose" },
    { key: "html",    icon: <CodeOutlined />,  label: "HTML"    },
    { key: "preview", icon: <EyeOutlined />,   label: "Preview" },
  ];

  const modeTabs = (
    <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 6, padding: 2 }}>
      {MODES.map(({ key, icon, label }) => (
        <Button key={key} size="small" icon={icon} onClick={() => setBodyMode(key)}
          style={{ borderRadius: 4, border: "none", fontSize: 11, fontWeight: 600, boxShadow: "none",
            background: bodyMode === key ? "#302b63" : "transparent",
            color: bodyMode === key ? "#fff" : "#374151" }}>
          {label}
        </Button>
      ))}
    </div>
  );

  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden", background: "#fff" }}>

      {/* Toolbar — only in compose mode */}
      {bodyMode === "compose" && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
          {btn(<BoldOutlined />, "bold", null, "Bold")}
          {btn(<ItalicOutlined />, "italic", null, "Italic")}
          {btn(<UnderlineOutlined />, "underline", null, "Underline")}
          <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
          {btn(<UnorderedListOutlined />, "insertUnorderedList", null, "Bullet list")}
          {btn(<OrderedListOutlined />, "insertOrderedList", null, "Numbered list")}
          <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
          {btn(<AlignLeftOutlined />, "justifyLeft", null, "Align left")}
          {btn(<AlignCenterOutlined />, "justifyCenter", null, "Align center")}
          {btn(<AlignRightOutlined />, "justifyRight", null, "Align right")}
          <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
          <Tooltip title="Insert link">
            <button type="button"
              onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL"); if (url) exec("createLink", url); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={(e) => e.currentTarget.style.background = "none"}
            ><LinkOutlined /></button>
          </Tooltip>
          {btn(<UndoOutlined />, "undo", null, "Undo")}
          {btn(<RedoOutlined />, "redo", null, "Redo")}
          <div style={{ flex: 1 }} />
          {modeTabs}
        </div>
      )}

      {/* Header with mode tabs — html / preview modes */}
      {bodyMode !== "compose" && (
        <div style={{ display: "flex", alignItems: "center", padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
          <div style={{ flex: 1 }} />
          {modeTabs}
        </div>
      )}

      {/* Compose — contentEditable rich text */}
      {bodyMode === "compose" && (
        <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={emit}
          data-placeholder={placeholder}
          style={{ minHeight: 260, padding: "12px 14px", outline: "none", fontSize: 13, lineHeight: 1.8, color: "#1a1a2e", overflowY: "auto" }} />
      )}

      {/* HTML — monospace textarea, direct bodyHtml edit */}
      {bodyMode === "html" && (
        <textarea value={value} onChange={(e) => onChange?.(e.target.value)} rows={12}
          placeholder="Paste or write your HTML here…"
          style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: "none", outline: "none", fontFamily: "monospace", fontSize: 12, lineHeight: 1.6, color: "#1a1a2e", resize: "vertical", background: "#f8fafc" }} />
      )}

      {/* Preview — isolated iframe, zero style leakage */}
      {bodyMode === "preview" && (
        value ? (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <iframe srcDoc={value}
              style={{ width: "100%", border: "none", display: "block", minHeight: 260 }}
              onLoad={(e) => { const doc = e.target.contentDocument; if (doc) e.target.style.height = doc.body.scrollHeight + 16 + "px"; }}
              title="email-body-preview" sandbox="allow-same-origin" />
          </div>
        ) : (
          <div style={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, color: "#9ca3af", fontSize: 13 }}>
            <InboxOutlined style={{ fontSize: 32, color: "#d1d5db" }} />
            <span>Nothing to preview — compose a message or select a template</span>
          </div>
        )
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function BulkEmailCompose() {
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTemplate = location.state?.template ?? null;

  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyMode, setBodyMode] = useState("compose");
  const [bodyError, setBodyError] = useState(false);
  const [subjectError, setSubjectError] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [sending, setSending] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [customerFilter, setCustomerFilter] = useState("");
  const searchTimer = useRef(null);

  /* Which audience the recipient panel is showing. Customers and leads live in
     different tables with different ids, so they are two lists and two
     selections rather than one merged list — merging them would make a lead
     with id 5 indistinguishable from a customer with id 5. */
  const [audience, setAudience] = useState("customers");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsLoadingMore, setLeadsLoadingMore] = useState(false);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadPage, setLeadPage] = useState(1);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadHasMore, setLeadHasMore] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadSelectAllLoading, setLeadSelectAllLoading] = useState(false);
  const leadSearchTimer = useRef(null);


  /* "customer:12" / "lead:12" rather than a bare id: the two id spaces overlap,
     and the preview call has to say which one it is sending. */
  const [previewTarget, setPreviewTarget] = useState(null);
  // "resolved" = the mail as this recipient gets it — the default (2026-10-27, user: "bydefault
  // preview dikhao not placeholders"). "raw" shows the placeholders.
  const [previewMode, setPreviewMode] = useState("resolved");
  const [previewData, setPreviewData] = useState({ subject: "", body: "" });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  /* "Custom" (2026-10-27): the user's own message inside a company design (email widget).
     A saved template that has a design is shown the same way — message only, design around it. */
  const [mode, setMode] = useState("template");
  const [widgetId, setWidgetId] = useState(null);
  const { data: widgets = [], isLoading: widgetsLoading } = useActiveEmailWidgets();
  const activeWidget = mode === "custom"
    ? widgets.find((w) => w.id === widgetId) || null
    : (selectedTemplate?.widget_id ? widgets.find((w) => w.id === selectedTemplate.widget_id) || null : null);
  // What the server wraps the body in: the picked design, or the chosen template's own one.
  const sendWidgetId = mode === "custom" ? widgetId : (selectedTemplate?.widget_id || null);
  // A widget with a {{greeting}} slot lets the sender write the opening line (2026-10-28).
  const [greeting, setGreeting] = useState(DEFAULT_GREETING);
  const greetingSlot = Boolean(activeWidget && hasGreetingSlot(activeWidget.html));
  const greetingField = greetingSlot ? { greeting } : {};

  // One design → it is the design; no need to make the user pick it.
  useEffect(() => {
    if (mode === "custom" && !widgetId && widgets.length === 1) setWidgetId(widgets[0].id);
  }, [mode, widgets, widgetId]);

  const switchMode = (next) => {
    handleClearTemplate();
    setWidgetId(null);
    setBodyError(false);
    setMode(next);
  };

  // load templates + customers on mount
  useEffect(() => {
    const token = getSessionToken();
    axios.get(`${API_BASE_URL}/get-email-template-list`, { headers: { ...authHeaders() } })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        if (Array.isArray(data)) setTemplates(data.filter((t) => t.status === "active"));
      }).catch(() => {});
    fetchCustomers("", 1);

    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate);
      setBodyHtml(defaultTemplate.body || "");
      setTimeout(() => form.setFieldsValue({ template: defaultTemplate.id, subject: defaultTemplate.subject }), 0);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const first = selectedKeys.length > 0
      ? `customer:${selectedKeys[0]}`
      : selectedLeadIds.length > 0 ? `lead:${selectedLeadIds[0]}` : null;
    if (first && !previewTarget) {
      setPreviewTarget(first);
      if (previewMode === "resolved") fetchPreview(first);
    }
    if (!first) { setPreviewTarget(null); setPreviewData({ subject: "", body: "" }); }
  }, [selectedKeys, selectedLeadIds]); // eslint-disable-line

  // The preview follows the writing: a short pause after the subject, the message or the design
  // changes, it is resolved again for the recipient shown.
  const watchedSubject = Form.useWatch("subject", form);
  useEffect(() => {
    if (previewMode !== "resolved" || !previewTarget) return undefined;
    const t = setTimeout(() => fetchPreview(previewTarget), 600);
    return () => clearTimeout(t);
  }, [watchedSubject, bodyHtml, sendWidgetId, greeting]); // eslint-disable-line

  const buildFilterParams = (filter) => {
    if (!filter) return {};
    if (filter === "trial") return { customer_type: "trial" };
    return { customer_status: filter };
  };

  const fetchCustomers = useCallback(async (search, page, append = false, filter = customerFilter) => {
    if (page === 1) setCustomersLoading(true); else setLoadingMore(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/subscription/get-customer-list-v3`, {
        params: { page, limit: LIMIT, search: search || undefined, ...buildFilterParams(filter) },
        headers: { ...authHeaders() },
      });
      const data = res.data?.data ?? [];
      const total = res.data?.total ?? 0;
      setCustomers((prev) => append ? [...prev, ...data] : data);
      setCustomerTotal(total);
      setHasMore(page * LIMIT < total);
    } catch { if (!append) setCustomers([]); }
    finally { setCustomersLoading(false); setLoadingMore(false); }
  }, [customerFilter]); // eslint-disable-line

  /**
   * The lead list, from the same endpoint the Leads screens use.
   *
   * `is_assigned: 1` matches those screens, and the endpoint scopes by token —
   * a rep sees their own leads, a manager their team's, a super-admin all — so
   * no owner is forced here.
   */
  const fetchLeads = useCallback(async (search, page, append = false) => {
    if (page === 1) setLeadsLoading(true); else setLeadsLoadingMore(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        params: { page, limit: LIMIT, is_assigned: 1, search: search || "" },
        headers: { ...authHeaders() },
      });
      const data = res.data?.data ?? [];
      const total = res.data?.total_records ?? res.data?.pagination?.total_records ?? data.length;
      setLeads((prev) => append ? [...prev, ...data] : data);
      setLeadTotal(total);
      setLeadHasMore(page * LIMIT < total);
    } catch { if (!append) setLeads([]); }
    finally { setLeadsLoading(false); setLeadsLoadingMore(false); }
  }, []);

  const handleLeadSearch = useCallback((e) => {
    const val = e.target.value;
    setLeadSearch(val);
    clearTimeout(leadSearchTimer.current);
    leadSearchTimer.current = setTimeout(() => { setLeadPage(1); fetchLeads(val, 1, false); }, 300);
  }, [fetchLeads]);

  const handleLeadScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (leadsLoadingMore || !leadHasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      const next = leadPage + 1;
      setLeadPage(next);
      fetchLeads(leadSearch, next, true);
    }
  }, [leadsLoadingMore, leadHasMore, leadPage, leadSearch, fetchLeads]);

  const handleLeadToggle = useCallback((id) => {
    setSelectedLeadIds((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]);
  }, []);

  /* Only the ones with an address: a lead with no email cannot be mailed, and
     selecting it would just make the send count wrong. */
  const handleLeadSelectAll = useCallback(async (checked) => {
    if (!checked) { setSelectedLeadIds([]); return; }
    setLeadSelectAllLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        params: { page: 1, limit: 9999, is_assigned: 1, search: leadSearch || "" },
        headers: { ...authHeaders() },
      });
      setSelectedLeadIds((res.data?.data ?? []).filter((l) => l.email).map((l) => l.id));
    } catch {}
    finally { setLeadSelectAllLoading(false); }
  }, [leadSearch]);

  const handleSearch = useCallback((e) => {
    const val = e.target.value;
    setCustomerSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setCustomerPage(1); fetchCustomers(val, 1, false); }, 300);
  }, [fetchCustomers]);

  const handleListScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      const next = customerPage + 1;
      setCustomerPage(next);
      fetchCustomers(customerSearch, next, true);
    }
  }, [loadingMore, hasMore, customerPage, customerSearch, fetchCustomers]);

  const handleToggle = useCallback((key) => {
    setSelectedKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  }, []);

  const handleSelectAll = useCallback(async (checked) => {
    if (!checked) { setSelectedKeys([]); return; }
    setSelectAllLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/subscription/get-customer-list-v3`, {
        params: { page: 1, limit: 9999, search: customerSearch || undefined, ...buildFilterParams(customerFilter) },
        headers: { ...authHeaders() },
      });
      setSelectedKeys((res.data?.data ?? []).map((c) => c.user_id));
    } catch {}
    finally { setSelectAllLoading(false); }
  }, [customerSearch, customerFilter]);

  const handleTemplateSelect = (id) => {
    const tmpl = templates.find((t) => t.id === id);
    setSelectedTemplate(tmpl);
    if (tmpl) {
      setBodyHtml(tmpl.body || "");
      // A widget template brings its own greeting; the sender may still change it.
      setGreeting(tmpl.widget_greeting || DEFAULT_GREETING);
      setBodyMode("preview");
      setBodyError(false);
      form.setFieldsValue({ subject: tmpl.subject });
      setPreviewMode("resolved");
      setPreviewError(false);
      if (previewTarget) fetchPreview(previewTarget, tmpl.subject, tmpl.body || "");
    }
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
    setBodyHtml("");
    setBodyMode("compose");
    form.setFieldsValue({ template: undefined, subject: "" });
    setPreviewData({ subject: "", body: "" });
  };

  const fetchPreview = async (target, subjectOverride, bodyOverride) => {
    let subject = subjectOverride !== undefined ? subjectOverride : (form.getFieldValue("subject") || "");
    let body = bodyOverride !== undefined ? bodyOverride : (bodyHtml || "");
    if (!target) return;
    const [kind, rawId] = String(target).split(":");
    const id = Number(rawId);
    // Nothing written yet: sample words stand in (preview only — never sent), so the recipient's
    // own details still resolve in the design instead of showing {{customer_name}}.
    if (!subject.trim()) subject = SAMPLE_SUBJECT;
    if (!body.replace(/<[^>]*>/g, "").trim()) body = SAMPLE_BODY;
    setPreviewLoading(true);
    setPreviewError(false);
    try {
      const res = await axios.post(`${EMAIL_API_URL}/api/bulk-email/preview`,
        /* A lead is not a user, so it goes under its own key. The server
           resolves the placeholders either way; it just needs to be told which
           record to read them from. */
        { subject, body, ...(kind === "lead" ? { lead_id: id } : { user_id: id }), ...(sendWidgetId ? { widget_id: sendWidgetId } : {}), ...greetingField },
        { headers: { ...authHeaders() } });
      const result = res.data?.data ?? res.data;
      if (result && (result.subject !== undefined || result.body !== undefined)) setPreviewData(result);
      else setPreviewError(true);
    } catch { setPreviewError(true); }
    finally { setPreviewLoading(false); }
  };

  const highlightRaw = (html) =>
    html.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      `<span style="background:#fff7e6;color:#854F0B;border:1px solid #FAC775;border-radius:4px;padding:1px 5px;font-size:12px;font-family:monospace;">{{${k}}}</span>`);

  const handleSend = async () => {
    const subject = form.getFieldValue("subject")?.trim();
    const strippedBody = bodyHtml.replace(/<[^>]*>/g, "").trim();
    let hasError = false;
    if (!subject) { setSubjectError(true); hasError = true; }
    if (!strippedBody) { setBodyError(true); hasError = true; }
    if (hasError) return;
    if (selectedKeys.length + selectedLeadIds.length === 0) {
      message.warning("Please select at least one recipient.");
      return;
    }
    if (mode === "custom" && !widgetId) { message.warning("Choose an email widget."); return; }
    setSending(true);
    try {
      const toBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ filename: f.name, content: reader.result.split(",")[1] });
        reader.onerror = reject;
        reader.readAsDataURL(f.originFileObj || f);
      });
      const attachments = await Promise.all(fileList.map(toBase64));
      await axios.post(`${EMAIL_API_URL}/api/bulk-email/send`, {
        recipient_ids: selectedKeys,
        /* Leads travel under their own key rather than mixed into
           recipient_ids: the two are different tables, and an id that could
           mean either would be resolved against the wrong one. */
        lead_ids: selectedLeadIds,
        subject, body: bodyHtml, attachments,
        template_id: selectedTemplate?.id || null,
        ...(sendWidgetId ? { widget_id: sendWidgetId } : {}),
        ...greetingField,
      }, { headers: { ...authHeaders() } });
      message.success({ content: `Email sent to ${selectedKeys.length + selectedLeadIds.length} recipients!`, icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />, duration: 4 });
      navigate("/send-bulk-mail", { state: { triggerPoll: true } });
    } catch (err) {
      // 422: the company design was switched off or deleted meanwhile — say so.
      message.error(err?.response?.status === 422 && err.response.data?.message
        ? err.response.data.message
        : "Failed to send email. Please try again.");
      setSending(false);
    }
  };

  const totalSelected = selectedKeys.length + selectedLeadIds.length;
  const onLeads = audience === "leads";

  /* Everyone selected, from either list, under the composite key the preview
     call reads. Built from the rows already loaded, so a recipient picked and
     then scrolled past still has a name to show. */
  const previewOptions = [
    ...customers.filter((c) => selectedKeys.includes(c.user_id))
      .map((c) => ({ value: `customer:${c.user_id}`, label: c.customer_name })),
    ...leads.filter((l) => selectedLeadIds.includes(l.id))
      .map((l) => ({ value: `lead:${l.id}`, label: `${l.name || "Unnamed lead"} (lead)` })),
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <button type="button" onClick={() => navigate(-1)}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "#f4f6fa", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13, color: "#374151", fontWeight: 500 }}>
              <ArrowLeftOutlined /> Back
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg,#302b63,#24243e)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <EditOutlined style={{ color: "#fff", fontSize: 17 }} />
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>Compose Email</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>Select recipients, choose a template or write manually</div>
              </div>
            </div>
            {totalSelected > 0 && (
              <Tag style={{ background: "#302b6315", border: "1px solid #302b6330", color: "#302b63", borderRadius: 20, padding: "4px 14px", fontSize: 13, marginLeft: "auto" }}>
                {totalSelected} Recipients selected
                {selectedKeys.length > 0 && selectedLeadIds.length > 0
                  && ` · ${selectedKeys.length} customers, ${selectedLeadIds.length} leads`}
              </Tag>
            )}
          </div>

          {/* 3-column layout */}
          <div style={{ display: "grid", gridTemplateColumns: "300px 1fr 380px", gap: 16, alignItems: "start" }}>

            {/* ── Col 1: Customer Selector ── */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f8", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 160px)" }}>
              <div style={{ padding: "16px 16px 10px", borderBottom: "1px solid #f5f5fc" }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a2e", marginBottom: 10 }}>
                  <UserOutlined style={{ marginRight: 6, color: "#302b63" }} />Select Recipients
                </div>

                {/* Customers and leads are two different audiences, so the panel
                    switches between them rather than listing both at once —
                    their filters and their ids have nothing in common. A
                    selection made on one side survives the switch. */}
                <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 8, padding: 2, marginBottom: 10 }}>
                  {[
                    { key: "customers", label: "Customers", count: selectedKeys.length },
                    { key: "leads", label: "Leads", count: selectedLeadIds.length },
                  ].map((tab) => {
                    const active = audience === tab.key;
                    return (
                      <button key={tab.key} type="button"
                        onClick={() => {
                          setAudience(tab.key);
                          // Fetched on first view, not on mount: most sends never open the other tab.
                          if (tab.key === "leads" && leads.length === 0 && !leadsLoading) fetchLeads("", 1, false);
                        }}
                        style={{
                          flex: 1, padding: "5px 0", borderRadius: 6, border: "none", cursor: "pointer",
                          fontSize: 12, fontWeight: 600,
                          background: active ? "#302b63" : "transparent",
                          color: active ? "#fff" : "#475569",
                        }}>
                        {tab.label}{tab.count > 0 ? ` (${tab.count})` : ""}
                      </button>
                    );
                  })}
                </div>

                <Input placeholder={onLeads ? "Search leads..." : "Search customers..."}
                  prefix={<SearchOutlined style={{ color: "#ccc" }} />}
                  value={onLeads ? leadSearch : customerSearch}
                  onChange={onLeads ? handleLeadSearch : handleSearch}
                  size="small" style={{ borderRadius: 8, marginBottom: 10 }} allowClear />

                {/* The status chips are subscription states, which a lead does
                    not have — so they belong to the customers tab only. */}
                <div style={{ display: onLeads ? "none" : "flex", flexWrap: "wrap", gap: 5 }}>
                  {[
                    { value: "", label: "All", color: "#302b63", bg: "#302b6315", border: "#302b6340" },
                    { value: "paid_active", label: "Paid", color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
                    { value: "paid_expired", label: "Expired", color: "#b91c1c", bg: "#fef2f2", border: "#fca5a5" },
                    { value: "trial", label: "Free Trial", color: "#0369a1", bg: "#f0f9ff", border: "#7dd3fc" },
                    { value: "payment_pending", label: "Pending", color: "#92400e", bg: "#fffbeb", border: "#fcd34d" },
                    { value: "expiring_soon", label: "Expiring", color: "#7c3aed", bg: "#faf5ff", border: "#c4b5fd" },
                  ].map((opt) => {
                    const active = customerFilter === opt.value;
                    return (
                      <div key={opt.value} onClick={() => {
                        const f = active ? "" : opt.value;
                        setCustomerFilter(f);
                        setCustomerPage(1);
                        setSelectedKeys([]);
                        fetchCustomers(customerSearch, 1, false, f);
                      }} style={{
                        padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                        cursor: "pointer", userSelect: "none",
                        color: active ? "#fff" : opt.color,
                        background: active ? opt.color : opt.bg,
                        border: `1px solid ${active ? opt.color : opt.border}`,
                        transition: "all 0.15s",
                      }}>
                        {opt.label}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  {onLeads ? (
                    <>
                      <Checkbox
                        checked={leadTotal > 0 && selectedLeadIds.length >= leadTotal}
                        indeterminate={selectedLeadIds.length > 0 && selectedLeadIds.length < leadTotal}
                        disabled={leadSelectAllLoading}
                        onChange={(e) => handleLeadSelectAll(e.target.checked)}
                      >
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{leadSelectAllLoading ? "Selecting..." : "Select All"}</span>
                      </Checkbox>
                      <span style={{ fontSize: 11, color: "#999" }}>{selectedLeadIds.length}/{leadTotal} selected</span>
                    </>
                  ) : (
                    <>
                      <Checkbox
                        checked={customerTotal > 0 && selectedKeys.length >= customerTotal}
                        indeterminate={selectedKeys.length > 0 && selectedKeys.length < customerTotal}
                        disabled={selectAllLoading}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      >
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{selectAllLoading ? "Selecting..." : "Select All"}</span>
                      </Checkbox>
                      <span style={{ fontSize: 11, color: "#999" }}>{selectedKeys.length}/{customerTotal} selected</span>
                    </>
                  )}
                </div>
              </div>

              {onLeads ? (
              <div className="bulk-email-customer-list" style={{ overflowY: "auto", flex: 1, padding: "8px 10px" }} onScroll={handleLeadScroll}>
                {leadsLoading
                  ? <div style={{ padding: 20, textAlign: "center", color: "#bbb", fontSize: 12 }}>Loading...</div>
                  : leads.length === 0
                    ? <div style={{ padding: 20, textAlign: "center", color: "#bbb", fontSize: 12 }}>No leads found</div>
                    : leads.map((l) => {
                      const sel = selectedLeadIds.includes(l.id);
                      /* A lead with no address cannot be mailed. Shown but not
                         selectable, so the list still matches the Leads screen
                         and nobody wonders where a row went. */
                      const mailable = Boolean(l.email);
                      return (
                        <div key={l.id} onClick={() => mailable && handleLeadToggle(l.id)}
                          title={mailable ? undefined : "This lead has no email address"}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, cursor: mailable ? "pointer" : "not-allowed", marginBottom: 3, background: sel ? "#302b6312" : "transparent", border: sel ? "1.5px solid #302b6330" : "1.5px solid transparent", opacity: mailable ? 1 : 0.5, transition: "all 0.15s" }}>
                          <Checkbox checked={sel} disabled={!mailable} onChange={() => handleLeadToggle(l.id)} onClick={(e) => e.stopPropagation()} />
                          <Avatar size={28} style={{ background: ["#302b6322","#e87a4e22","#52c41a22","#1890ff22"][l.id % 4], color: ["#302b63","#e87a4e","#52c41a","#1890ff"][l.id % 4], fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                            {(l.name || "?")[0]}
                          </Avatar>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name || "Unnamed lead"}</div>
                            <div style={{ fontSize: 11, color: mailable ? "#aaa" : "#d97706", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {l.email || "No email address"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                {leadsLoadingMore && <div style={{ textAlign: "center", padding: "8px 0", fontSize: 11, color: "#aaa" }}>Loading more...</div>}
                {!leadHasMore && leads.length > 0 && <div style={{ textAlign: "center", padding: "8px 0", fontSize: 11, color: "#ccc" }}>All {leadTotal} leads loaded</div>}
              </div>
              ) : (
              <div className="bulk-email-customer-list" style={{ overflowY: "auto", flex: 1, padding: "8px 10px" }} onScroll={handleListScroll}>
                {customersLoading
                  ? <div style={{ padding: 20, textAlign: "center", color: "#bbb", fontSize: 12 }}>Loading...</div>
                  : customers.map((c) => {
                    const sel = selectedKeys.includes(c.user_id);
                    return (
                      <div key={c.user_id} onClick={() => handleToggle(c.user_id)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, cursor: "pointer", marginBottom: 3, background: sel ? "#302b6312" : "transparent", border: sel ? "1.5px solid #302b6330" : "1.5px solid transparent", transition: "all 0.15s" }}>
                        <Checkbox checked={sel} onChange={() => handleToggle(c.user_id)} onClick={(e) => e.stopPropagation()} />
                        <Avatar size={28} style={{ background: ["#302b6322","#e87a4e22","#52c41a22","#1890ff22"][c.user_id % 4], color: ["#302b63","#e87a4e","#52c41a","#1890ff"][c.user_id % 4], fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                          {(c.customer_name || "?")[0]}
                        </Avatar>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.customer_name}</div>
                          <div style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.customer_email}</div>
                        </div>
                      </div>
                    );
                  })}
                {loadingMore && <div style={{ textAlign: "center", padding: "8px 0", fontSize: 11, color: "#aaa" }}>Loading more...</div>}
                {!hasMore && customers.length > 0 && <div style={{ textAlign: "center", padding: "8px 0", fontSize: 11, color: "#ccc" }}>All {customerTotal} customers loaded</div>}
              </div>
              )}
            </div>

            {/* ── Col 2: Email Form ── */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f8", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", padding: "24px 28px" }}>
              <Form form={form} layout="vertical" requiredMark={false}>

                {/* How the mail is written: a saved template, or Custom (the company design) */}
                <ComposeModeCards mode={mode} onChange={switchMode} customDisabled={!widgetsLoading && widgets.length === 0} />

                {/* Custom: which company design */}
                {mode === "custom" && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14, marginBottom: 8 }}>Email widget</div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {widgets.map((w) => {
                        const picked = w.id === widgetId;
                        return (
                          <button key={w.id} type="button" aria-pressed={picked} aria-label={`Use design ${w.name}`}
                            onClick={() => setWidgetId(w.id)}
                            style={{ padding: 6, borderRadius: 12, cursor: "pointer", background: picked ? "#f5f3ff" : "#fff", border: `2px solid ${picked ? "#5b48e8" : "#e5e7eb"}`, boxShadow: picked ? "0 4px 14px rgba(91,72,232,0.18)" : "none", transition: "all 0.15s" }}>
                            <WidgetFrame html={previewWidget(w.html, SAMPLE_BODY, SAMPLE_SUBJECT)} title={`${w.name} thumbnail`} scale={0.25} height={110} />
                            <div style={{ fontSize: 12, fontWeight: 600, color: picked ? "#5b48e8" : "#374151", marginTop: 6, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {w.name}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Template */}
                {mode === "template" && (<>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <ThunderboltOutlined style={{ color: "#faad14", fontSize: 14 }} />
                      <span style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14 }}>Email Template</span>
                      <Tag style={{ background: "#fff7e6", color: "#faad14", border: "1px solid #ffd591", borderRadius: 10, fontSize: 11, margin: 0 }}>Optional</Tag>
                    </div>
                    {selectedTemplate && (
                      <Button size="small" type="link" danger onClick={handleClearTemplate} style={{ padding: 0, fontSize: 12, height: "auto" }}>Clear template</Button>
                    )}
                  </div>
                  <Select value={selectedTemplate?.id || undefined} placeholder="Choose a template to auto-fill subject & body..."
                    onChange={handleTemplateSelect} size="large" style={{ width: "100%" }} allowClear onClear={handleClearTemplate}
                    getPopupContainer={() => document.body} optionLabelProp="label">
                    {templates.map((t) => (
                      <Option key={t.id} value={t.id} label={t.template_name}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{t.template_name}</div>
                        <div style={{ fontSize: 11, color: "#aaa" }}>{(t.subject || "").substring(0, 50)}...</div>
                      </Option>
                    ))}
                  </Select>
                </div>

                {selectedTemplate && (
                  <Alert type="info" showIcon message={`Template "${selectedTemplate.template_name}" applied`}
                    style={{ marginBottom: 16, borderRadius: 8, background: "#e6f4ff", border: "1px solid #91caff" }} />
                )}
                </>)}

                <Divider style={{ margin: "4px 0 20px", borderColor: "#f0f0f8" }} />

                {/* Subject */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14, marginBottom: 8 }}>Subject</div>
                  <Form.Item name="subject" noStyle>
                    <Input size="large" placeholder="Enter a subject..." className="bulk-email-input"
                      disabled={false}
                      style={{ borderRadius: 10, borderColor: subjectError ? "#ff4d4f" : undefined }}
                      onChange={() => setSubjectError(false)}
                      suffix={<Tooltip title="Use {{first_name}}, {{company_name}} for personalization"><span style={{ color: "#ccc", fontSize: 12, cursor: "help" }}>{"{ }"}</span></Tooltip>}
                    />
                  </Form.Item>
                  {subjectError && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>Please enter a subject</div>}
                </div>

                {/* Greeting — the widget's opening line, the sender's to change (2026-10-28) */}
                {greetingSlot && (
                  <div style={{ marginBottom: 20 }}>
                    <label htmlFor="bulk-greeting" style={{ display: "block", fontWeight: 600, color: "#1a1a2e", fontSize: 14, marginBottom: 8 }}>Greeting</label>
                    <Input id="bulk-greeting" size="large" value={greeting} onChange={(e) => setGreeting(e.target.value)}
                      placeholder={DEFAULT_GREETING} maxLength={150} style={{ borderRadius: 10 }} />
                    <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 5 }}>
                      The opening line. <span style={{ fontFamily: "monospace" }}>{"{{customer_name}}"}</span> fills in per recipient (a lead's name for leads) — or write e.g. "Dear Sir/Madam,".
                    </div>
                  </div>
                )}

                {/* Body */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14 }}>Email Body</span>
                    <Tag style={{ background: "#f0f5ff", color: "#2f54eb", border: "1px solid #adc6ff", borderRadius: 8, fontSize: 11, margin: 0 }}>Supports variables</Tag>
                  </div>
                  {activeWidget
                    ? <WidgetBodyEditor widget={activeWidget} value={bodyHtml} subject={form.getFieldValue("subject") || ""}
                        onChange={(html) => { setBodyHtml(html); setBodyError(false); }} placeholder="Write your message here..." />
                    : <BodyEditor value={bodyHtml} onChange={(html) => { setBodyHtml(html); setBodyError(false); }} placeholder="Write your email content here..." bodyMode={bodyMode} setBodyMode={setBodyMode} />}
                  {bodyError && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>Please write the email body</div>}
                </div>

                {/* Attachments */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <PaperClipOutlined style={{ color: "#1a1a2e", fontSize: 14 }} />
                    <span style={{ fontWeight: 600, color: "#1a1a2e", fontSize: 14 }}>Attachments</span>
                    <Tag style={{ background: "#f6ffed", color: "#52c41a", border: "1px solid #b7eb8f", borderRadius: 10, fontSize: 11, margin: 0 }}>Optional</Tag>
                  </div>
                  <Dragger fileList={[]} onChange={({ fileList: fl }) => setFileList(fl)} multiple beforeUpload={(file) => { setFileList(prev => [...prev, file]); return false; }}
                    style={{ borderRadius: 10, background: "#fafafe" }}>
                    <div style={{ padding: "8px 0" }}>
                      <p style={{ fontSize: 22, margin: "0 0 4px" }}><InboxOutlined style={{ color: "#302b63" }} /></p>
                      <p style={{ fontWeight: 600, margin: "0 0 2px", color: "#302b63", fontSize: 13 }}>Drop files or click to upload</p>
                      <p style={{ color: "#bbb", fontSize: 11, margin: 0 }}>PDF, DOCX, XLSX, JPG, PNG — Max 10MB</p>
                    </div>
                  </Dragger>
                  {fileList.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                      {fileList.map((file, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#f5f3ff", border: "1px solid #e9e6ff", borderRadius: 8 }}>
                          <PaperClipOutlined style={{ color: "#302b63", fontSize: 13, flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 12, color: "#302b63", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                          <button type="button" onClick={() => setFileList(prev => prev.filter((_, i) => i !== idx))}
                            style={{ background: "#fee2e2", border: "none", borderRadius: 6, width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 13, flexShrink: 0 }}>
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 4 }}>
                  <Button size="large" onClick={() => navigate(-1)} style={{ borderRadius: 10, padding: "0 28px" }}>Cancel</Button>
                  <Button type="primary" size="large" loading={sending} onClick={handleSend} icon={<SendOutlined />}
                    style={{ borderRadius: 10, padding: "0 36px", background: "linear-gradient(135deg,#302b63,#24243e)", border: "none", fontWeight: 600, fontSize: 15, boxShadow: "0 4px 16px rgba(48,43,99,0.4)" }}>
                    {sending ? "Sending..." : `Send${totalSelected > 0 ? ` to ${totalSelected} Recipients` : " Email"}`}
                  </Button>
                </div>
              </Form>
            </div>

            {/* ── Col 3: Preview ── */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f8", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden", position: "sticky", top: 80 }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #f0f0f8", background: "#fafafe", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: totalSelected > 0 ? "#52c41a" : "#d9d9d9" }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>Email Preview</span>
                <Tag style={{ background: "#f6ffed", color: "#389e0d", border: "1px solid #b7eb8f", borderRadius: 10, fontSize: 11, margin: 0 }}>Per recipient</Tag>
              </div>

              {totalSelected === 0 && activeWidget
                // A company design is previewed before anyone is picked — placeholders unresolved.
                ? <div>
                    <div style={{ maxHeight: 520, overflowY: "auto" }}>
                      <WidgetFrame html={previewWidget(activeWidget.html, highlightRaw(bodyHtml || SAMPLE_BODY), form.getFieldValue("subject") || SAMPLE_SUBJECT, greeting)} title="Email preview" />
                    </div>
                    <div style={{ padding: "8px 14px", borderTop: "0.5px solid #f0f0f8", background: "#fafafe", fontSize: 11, color: "#aaa" }}>
                      Select recipients to see it per person
                    </div>
                  </div>
                : totalSelected === 0
                ? <div style={{ padding: "48px 24px", textAlign: "center", color: "#bbb" }}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>👆</div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Select recipients to preview</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Placeholders will be resolved per user</div>
                  </div>
                : <>
                  {/* Controls */}
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #f0f0f8", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["raw", "resolved"].map((mode) => (
                        <button key={mode} type="button"
                          onClick={() => { setPreviewMode(mode); setPreviewError(false); if (mode === "resolved" && previewTarget) fetchPreview(previewTarget); }}
                          style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: "pointer", border: "0.5px solid", borderColor: previewMode === mode ? "#302b63" : "#d9d9d9", background: previewMode === mode ? "#302b63" : "transparent", color: previewMode === mode ? "#fff" : "#666" }}>
                          {mode === "raw" ? "Placeholders" : "Preview"}
                        </button>
                      ))}
                    </div>
                    <Select size="small" value={previewTarget} onChange={(t) => { setPreviewTarget(t); if (previewMode === "resolved") fetchPreview(t); }}
                      style={{ flex: 1, minWidth: 120, fontSize: 12 }} placeholder="Preview as..." getPopupContainer={() => document.body}>
                      {previewOptions.map((o) => (
                        <Option key={o.value} value={o.value}>{o.label}</Option>
                      ))}
                    </Select>
                  </div>

                  {/* Subject */}
                  <div style={{ padding: "8px 14px", borderBottom: "0.5px solid #f0f0f8", background: "#f5f3ff" }}>
                    <span style={{ fontSize: 11, color: "#aaa", marginRight: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>Subject</span>
                    <span style={{ fontSize: 13, color: "#1a1a2e", fontWeight: 500 }}
                      dangerouslySetInnerHTML={{ __html: previewLoading ? "Loading..." : previewMode === "resolved" && previewData.subject ? previewData.subject : highlightRaw(form.getFieldValue("subject") || "") }}
                    />
                  </div>

                  {/* Body */}
                  {previewError
                    ? <div style={{ padding: "14px 16px", fontSize: 12, color: "#d48806", background: "#fffbe6", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>⚠</span> Preview unavailable. Showing template with placeholders.
                      </div>
                    : activeWidget
                    // A full email (its own <style>) — in an iframe, so nothing leaks into the page.
                    ? <div style={{ maxHeight: 520, overflowY: "auto", opacity: previewLoading ? 0.5 : 1, transition: "opacity 0.2s" }}>
                        <WidgetFrame title="Email preview"
                          html={previewMode === "resolved" && previewData.body
                            ? previewData.body
                            : previewWidget(activeWidget.html, highlightRaw(bodyHtml || SAMPLE_BODY), form.getFieldValue("subject") || SAMPLE_SUBJECT, greeting)} />
                      </div>
                    : <div style={{ padding: "14px 16px", fontSize: 13, lineHeight: 1.8, color: "#1a1a2e", minHeight: 200, maxHeight: 420, overflowY: "auto", opacity: previewLoading ? 0.5 : 1, transition: "opacity 0.2s" }}
                        dangerouslySetInnerHTML={{ __html: previewLoading ? '<span style="color:#bbb">Resolving...</span>' : previewMode === "resolved" && previewData.body ? previewData.body : highlightRaw(bodyHtml) }}
                      />
                  }

                  {/* Footer */}
                  <div style={{ padding: "8px 14px", borderTop: "0.5px solid #f0f0f8", background: "#fafafe", fontSize: 11, color: "#aaa", display: "flex", justifyContent: "space-between" }}>
                    <span>{totalSelected} personalized copies</span>
                    {previewTarget && <span style={{ color: "#302b63", fontWeight: 500 }}>
                      {previewOptions.find((o) => o.value === previewTarget)?.label}
                    </span>}
                  </div>
                </>}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
